import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import VisibilityIcon from "@material-ui/icons/Visibility";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import StarIcon from "@material-ui/icons/Star";
import StarBorderIcon from "@material-ui/icons/StarBorder";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
  listKanbanBoards,
  listKanbanBoardTemplates,
  createKanbanBoard,
  updateKanbanBoard,
  setDefaultKanbanBoard,
  deleteKanbanBoard,
  storeBoardId
} from "../../services/kanbanBoardService";

const PALETTE = [
  "#3b82f6",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#a855f7",
  "#ec4899",
  "#14b8a6",
  "#6366f1",
  "#64748b"
];

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "auto",
    ...theme.scrollbarStyles
  },
  dot: {
    display: "inline-block",
    width: 12,
    height: 12,
    borderRadius: "50%"
  },
  description: {
    color: theme.palette.text.secondary,
    maxWidth: 420,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  palette: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 10,
    margin: theme.spacing(2, 0)
  },
  swatch: {
    width: 26,
    height: 26,
    borderRadius: "50%",
    cursor: "pointer",
    border: "3px solid transparent",
    padding: 0
  },
  swatchSelected: {
    borderColor: theme.palette.text.primary
  },
  customColor: {
    width: 46,
    height: 30,
    border: "none",
    padding: 0,
    background: "none",
    cursor: "pointer"
  },
  lanes: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginTop: theme.spacing(1)
  },
  template: {
    display: "block",
    width: "100%",
    textAlign: "left",
    padding: theme.spacing(1.25, 1.5),
    marginTop: theme.spacing(1),
    borderRadius: 8,
    border: `1px solid transparent`,
    background: "none",
    cursor: "pointer",
    font: "inherit",
    color: "inherit",
    "&:hover": {
      background: theme.palette.action.hover
    }
  },
  templateSelected: {
    borderColor: theme.palette.primary.main,
    background: theme.palette.type === "dark" ? "rgba(59,130,246,0.12)" : "#eff6ff",
    "&:hover": {
      background: theme.palette.type === "dark" ? "rgba(59,130,246,0.18)" : "#eff6ff"
    }
  },
  templateTitle: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontWeight: 600,
    fontSize: 13
  },
  laneChip: {
    color: "#fff",
    fontWeight: 600,
    fontSize: 11,
    height: 22
  }
}));

const emptyForm = { name: "", description: "", color: PALETTE[0], isShared: true };

const KanbanBoards = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const isAdmin = user?.profile === "admin";

  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onlyMine, setOnlyMine] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [lanes, setLanes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templateKey, setTemplateKey] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      setBoards(await listKanbanBoards(onlyMine));
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyMine]);

  const openNew = async () => {
    setEditing(null);
    setForm(emptyForm);
    setLanes([]);
    setTemplateKey("");
    setModalOpen(true);
    if (templates.length === 0) {
      try {
        setTemplates(await listKanbanBoardTemplates());
      } catch {
        setTemplates([]);
      }
    }
  };

  // Elegir una plantilla completa nombre, descripción y color, salvo que el
  // usuario ya los haya escrito a mano.
  const chooseTemplate = key => {
    const previous = templates.find(t => t.key === templateKey);
    const next = templates.find(t => t.key === key);
    setTemplateKey(key);
    setForm(current => {
      const untouched = field =>
        !current[field] || (previous && current[field] === previous[field]);
      if (!next) {
        return {
          ...current,
          name: previous && current.name === previous.name ? "" : current.name,
          description:
            previous && current.description === previous.description ? "" : current.description
        };
      }
      return {
        ...current,
        name: untouched("name") ? next.name : current.name,
        description: untouched("description") ? next.description : current.description,
        color: untouched("color") || current.color === emptyForm.color ? next.color : current.color
      };
    });
  };

  const openEdit = async board => {
    setEditing(board);
    setForm({
      name: board.name,
      description: board.description || "",
      color: board.color || PALETTE[0],
      isShared: board.isShared
    });
    setLanes([]);
    setModalOpen(true);
    try {
      const { data } = await api.get("/tag/kanban/", { params: { boardId: board.id } });
      setLanes(data.lista || []);
    } catch {
      setLanes([]);
    }
  };

  const openInKanban = board => {
    storeBoardId(board.id);
    history.push(`/kanban?board=${board.id}`);
  };

  const handleSave = async () => {
    if (form.name.trim().length < 2) {
      toast.error("El nombre debe tener al menos 2 caracteres.");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await updateKanbanBoard(editing.id, form);
        toast.success("Board actualizado.");
      } else {
        await createKanbanBoard({ ...form, ...(templateKey ? { template: templateKey } : {}) });
        toast.success("Board creado.");
      }
      setModalOpen(false);
      load();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDefault = async board => {
    if (board.isDefault) return;
    try {
      await setDefaultKanbanBoard(board.id);
      toast.success(`"${board.name}" es ahora el board predeterminado.`);
      load();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteKanbanBoard(deleting.id);
      toast.success("Board eliminado.");
      load();
    } catch (err) {
      toastError(err);
    } finally {
      setDeleting(null);
    }
  };

  // El predeterminado lo usa toda la empresa: no puede ser privado
  const sharedLocked = Boolean(editing?.isDefault);

  return (
    <MainContainer>
      <ConfirmationModal
        title={deleting ? `¿Eliminar el board "${deleting.name}"?` : ""}
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
      >
        {deleting && deleting.laneCount > 0
          ? `Se eliminarán también sus ${deleting.laneCount} columnas. Los tickets no se borran: solo dejan de estar en este board.`
          : "Esta acción no se puede deshacer."}
      </ConfirmationModal>

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? "Editar Board" : "Nuevo Board"}</DialogTitle>
        <DialogContent>
          <TextField
            label="Nombre del board"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            variant="outlined"
            size="small"
            fullWidth
            autoFocus
            margin="dense"
            inputProps={{ maxLength: 60 }}
          />
          <TextField
            label="Descripción"
            value={form.description}
            onChange={e => setForm({ ...form, description: e.target.value })}
            variant="outlined"
            size="small"
            fullWidth
            multiline
            minRows={2}
            margin="dense"
          />

          <div className={classes.palette}>
            <Typography variant="body2">
              <b>Color:</b>
            </Typography>
            {PALETTE.map(c => (
              <button
                type="button"
                key={c}
                aria-label={c}
                className={`${classes.swatch} ${form.color === c ? classes.swatchSelected : ""}`}
                style={{ background: c }}
                onClick={() => setForm({ ...form, color: c })}
              />
            ))}
            <Tooltip title="Otro color">
              <input
                type="color"
                className={classes.customColor}
                value={form.color}
                onChange={e => setForm({ ...form, color: e.target.value })}
              />
            </Tooltip>
          </div>

          <FormControlLabel
            control={
              <Switch
                checked={form.isShared}
                disabled={sharedLocked}
                onChange={e => setForm({ ...form, isShared: e.target.checked })}
                color="primary"
              />
            }
            label="Compartir con toda la empresa"
          />
          <Typography variant="caption" color="textSecondary" component="p" style={{ marginLeft: 48 }}>
            {sharedLocked ? (
              "Es el board predeterminado de la empresa: siempre es compartido."
            ) : form.isShared ? (
              <>
                <b>Compartido:</b> todos los usuarios de la empresa lo verán en el selector del Kanban.
              </>
            ) : (
              <>
                <b>Privado:</b> solo tú verás este board. Otros usuarios de la misma empresa no tendrán acceso a él en el selector del Kanban.
              </>
            )}
          </Typography>

          {!editing && templates.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <Typography variant="subtitle2">Plantilla (opcional)</Typography>
              <button
                type="button"
                className={`${classes.template} ${templateKey === "" ? classes.templateSelected : ""}`}
                onClick={() => chooseTemplate("")}
              >
                <div className={classes.templateTitle}>En blanco</div>
                <Typography variant="caption" color="textSecondary">
                  Crear board sin columnas predefinidas
                </Typography>
              </button>
              {templates.map(t => (
                <button
                  type="button"
                  key={t.key}
                  className={`${classes.template} ${templateKey === t.key ? classes.templateSelected : ""}`}
                  onClick={() => chooseTemplate(t.key)}
                >
                  <div className={classes.templateTitle}>
                    <span className={classes.dot} style={{ background: t.color }} />
                    {t.name}
                  </div>
                  <Typography variant="caption" color="textSecondary" component="div">
                    {t.description}
                  </Typography>
                  <div className={classes.lanes}>
                    {t.lanes.map(l => (
                      <Chip
                        key={l.name}
                        size="small"
                        label={l.name}
                        className={classes.laneChip}
                        style={{ background: l.color }}
                      />
                    ))}
                  </div>
                </button>
              ))}
            </div>
          )}

          {editing && (
            <div style={{ marginTop: 16 }}>
              <Typography variant="subtitle2">Columnas de este tablero</Typography>
              {lanes.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  Aún no hay columnas registradas — créalas desde la pantalla del Kanban
                  ("+ Agregar columnas") con este board seleccionado.
                </Typography>
              ) : (
                <div className={classes.lanes}>
                  {lanes.map(lane => (
                    <Chip
                      key={lane.id}
                      size="small"
                      label={lane.name}
                      style={{ background: lane.color, color: "#fff", textShadow: "1px 1px 1px #0006" }}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button onClick={handleSave} color="primary" variant="contained" disabled={saving}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <MainHeader>
        <Title>Boards del Kanban</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => history.push("/kanban")}
          >
            Volver al Kanban
          </Button>
          <FormControlLabel
            control={
              <Switch
                checked={onlyMine}
                onChange={e => setOnlyMine(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label="Mis boards"
          />
          <Button variant="contained" color="primary" onClick={openNew}>
            + Nuevo Board
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={40} />
              <TableCell>Nombre</TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell align="center">Columnas</TableCell>
              <TableCell align="center">Visibilidad</TableCell>
              <TableCell align="center">Predeterminado</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRowSkeleton columns={7} />
            ) : boards.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {onlyMine ? "Todavía no creaste ningún board." : "No hay boards."}
                </TableCell>
              </TableRow>
            ) : (
              boards.map(board => (
                <TableRow key={board.id} hover>
                  <TableCell>
                    <span className={classes.dot} style={{ background: board.color }} />
                  </TableCell>
                  <TableCell>
                    <b>{board.name}</b>
                  </TableCell>
                  <TableCell>
                    <div className={classes.description} title={board.description || ""}>
                      {board.description || "—"}
                    </div>
                  </TableCell>
                  <TableCell align="center">{board.laneCount}</TableCell>
                  <TableCell align="center">
                    <Chip
                      size="small"
                      label={board.isShared ? "Compartido" : "Privado"}
                      color={board.isShared ? "primary" : "default"}
                      variant={board.isShared ? "outlined" : "default"}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip
                      title={
                        board.isDefault
                          ? "Board predeterminado de la empresa"
                          : !isAdmin
                          ? "Solo un administrador puede cambiar el predeterminado"
                          : !board.isShared
                          ? "Un board privado no puede ser el predeterminado"
                          : "Marcar como predeterminado"
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleDefault(board)}
                          disabled={!board.isDefault && (!isAdmin || !board.isShared)}
                        >
                          {board.isDefault ? (
                            <StarIcon style={{ color: "#f59e0b" }} />
                          ) : (
                            <StarBorderIcon />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Abrir en el Kanban">
                      <IconButton size="small" onClick={() => openInKanban(board)}>
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    {board.canManage && (
                      <>
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => openEdit(board)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" onClick={() => setDeleting(board)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default KanbanBoards;
